import React, { useEffect, useRef, useState } from 'react';
import CitySearchInput from '../../geocoding/CitySearchInput';
import type { CityResult } from '../../geocoding/api';
import Input from '../../../components/ui/Input';
import { getCountryFlag } from '../../../shared/countryFlag';
import { formatDurationMin } from '../utils';
import type { StopSuggestion, StopSuggestionPick, StopSuggestionTarget } from '../../journeys/api';
import { analytics } from '../../../shared/analytics';
import { guestWallJustOpened } from '../../auth/guest';
// Circular on purpose (RouteTimeline renders this picker): Dots and
// useMinLoading are only read at render time, after both modules loaded.
import { Dots, useMinLoading } from './RouteTimeline';

/**
 * StopPlacePicker: the ONE place picker for adding a stop and for replacing
 * one on the route timeline (the "+" between stops, and a stop's Replace).
 * Two ways in, behind a toggle:
 *  - "Pick a city": the Mapbox autocomplete (CitySearchInput), as before;
 *  - "Describe a place": the user types the kind of place they want ("a
 *    lakeside town for hiking"), Jody suggests 2–3 real places, and tapping
 *    one picks it. Each card also links to a Google search for the place (new
 *    tab, so the suggestions are still here when the user comes back).
 * Hovering a suggestion previews it on the journey map (onPreview).
 * Either way the parent gets a geocoded CityResult and runs the usual
 * deterministic add/replace. A suggestion also carries `suggestion` (the
 * call's id and the card's rank) for the server-side pick event.
 *
 * The Describe toggle only shows when the parent passes `onSuggest` (the
 * read-only shared view doesn't). Backend: services/stopSuggestionService.js;
 * changelog docs/changelog/2026-10-03-describe-a-place.md; conventions
 * docs/codebase/ui-conventions.md ("Place pickers").
 */

/** Hovering (or focusing) a suggestion previews it on the journey map; null ends the preview. */
export type PreviewPlaceFn = (target: StopSuggestionTarget, place: StopSuggestion | null) => void;

export type SuggestPlacesFn = (
  target: StopSuggestionTarget,
  instructions: string,
) => Promise<{ requestId: string | null; suggestions: StopSuggestion[] }>;

type Mode = 'city' | 'describe';
type Status = 'idle' | 'loading' | 'done' | 'error';

const GREEN = '#157a55';
const INK = '#16241d';
const MUTED = '#6c7872';
const MAX_TEXT = 300;

// Same quiet chip style as the night-train radius picker (NightTrainsNearbyDialog).
const chip = (active: boolean): React.CSSProperties => ({
  font: 'inherit', fontSize: 12.5, fontWeight: 700, borderRadius: 999, padding: '6px 12px', cursor: 'pointer',
  border: `1px solid ${active ? GREEN : 'rgba(20,40,30,0.10)'}`,
  background: active ? '#e7f1ec' : '#fff',
  color: active ? '#0e5c40' : MUTED,
});
const linkBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: MUTED, fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0 };

/** The travel hint on a suggestion card. Durations as everywhere (formatDurationMin). */
function travelHint(t: StopSuggestion['travel'], action: StopSuggestionTarget['action'], replacing?: string): string | null {
  if (t.minutes == null) return null;
  if (t.kind === 'from' && t.city) return `${formatDurationMin(t.minutes)} from ${t.city}`;
  if (t.kind === 'to' && t.city) return `${formatDurationMin(t.minutes)} to ${t.city}`;
  if (Math.abs(t.minutes) < 15) return action === 'replace' ? `About the same travel as ${replacing || 'now'}` : 'On the way';
  if (t.minutes > 0) return `+${formatDurationMin(t.minutes)} of travel`;
  return `${formatDurationMin(-t.minutes)} less travel`;
}

// "Look it up on Google": the place's name, region and country, so the search
// lands on the right town (its knowledge panel: photos, map, Wikipedia).
const googleUrl = (s: StopSuggestion) => `https://www.google.com/search?q=${encodeURIComponent([s.city, s.region, s.country].filter(Boolean).join(' '))}`;
const ExternalIcon: React.FC = () => (
  <svg width={11} height={11} viewBox="0 0 24 24" fill="none" aria-hidden><path d="M14 5h5v5M19 5l-8 8M17 14v4a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h4" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" /></svg>
);

// A suggestion as the CityResult the add/replace handlers already take.
const asCity = (s: StopSuggestion): CityResult => ({
  id: `suggestion:${s.city}:${s.latitude},${s.longitude}`,
  name: s.city,
  displayName: [s.city, s.region, s.country].filter(Boolean).join(', '),
  country: s.country,
  region: s.region,
  kind: 'city',
  lat: s.latitude,
  lng: s.longitude,
});

export interface StopPlacePickerProps {
  /** Where the place goes; sent with the suggestion request. */
  target: StopSuggestionTarget;
  /** Small heading over the picker ("Add a stop here", "Replace 🇮🇹 Rome"). */
  title: React.ReactNode;
  /** Name of the stop being replaced, for the "same travel as …" hint. */
  replacingCity?: string;
  busy?: boolean;
  /** Present = the "Describe a place" toggle is offered. */
  onSuggest?: SuggestPlacesFn;
  /** Hover/focus preview of a suggestion on the journey map. */
  onPreview?: PreviewPlaceFn;
  onPick: (city: CityResult, suggestion?: StopSuggestionPick) => void;
  onCancel: () => void;
}

const StopPlacePicker: React.FC<StopPlacePickerProps> = ({ target, title, replacingCity, busy, onSuggest, onPreview, onPick, onCancel }) => {
  const [mode, setMode] = useState<Mode>('city');
  const [text, setText] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ requestId: string | null; suggestions: StopSuggestion[]; forText: string } | null>(null);
  const showLoader = useMinLoading(status === 'loading');
  // The map preview follows the pointer over the cards. It must not outlive
  // the picker (a pick or Cancel unmounts it), nor a list that was replaced.
  const previewRef = useRef({ target, onPreview, active: false });
  previewRef.current.target = target;
  previewRef.current.onPreview = onPreview;
  const preview = (s: StopSuggestion | null) => {
    const r = previewRef.current;
    if (!r.onPreview || (!s && !r.active)) return;
    r.active = Boolean(s);
    r.onPreview(r.target, s);
  };
  useEffect(() => () => { const r = previewRef.current; if (r.active && r.onPreview) r.onPreview(r.target, null); }, []);
  const canSuggest = text.trim().length >= 3 && status !== 'loading' && !busy;

  const suggest = async () => {
    if (!onSuggest || !canSuggest) return;
    const asked = text.trim();
    setStatus('loading');
    setError(null);
    preview(null);
    try {
      const res = await onSuggest(target, asked);
      setResult({ requestId: res.requestId, suggestions: res.suggestions || [], forText: asked });
      setStatus('done');
    } catch (err: any) {
      // A guest gets the sign-up wall (suggesting needs an account, like
      // every edit); no error line behind it.
      if (guestWallJustOpened()) { setStatus('idle'); return; }
      setError(err?.response?.data?.error || 'Jody could not suggest places right now. Please try again.');
      setStatus('error');
    }
  };

  const pick = (s: StopSuggestion, i: number) => {
    if (busy || !result) return;
    onPick(asCity(s), { requestId: result.requestId, rank: i + 1, count: result.suggestions.length });
  };

  const loading = status === 'loading' || showLoader;
  const list = !loading && status === 'done' && result ? result.suggestions : null;

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <div style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>{title}</div>

      {onSuggest && (
        <div role="radiogroup" aria-label="How to choose the place" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          <button type="button" role="radio" aria-checked={mode === 'city'} onClick={() => setMode('city')} style={chip(mode === 'city')}>Pick a city</button>
          <button type="button" role="radio" aria-checked={mode === 'describe'} onClick={() => setMode('describe')} style={chip(mode === 'describe')}>Describe a place</button>
        </div>
      )}

      {mode === 'city' ? (
        <CitySearchInput autoFocus placeholder="Type a city…" onSelect={(c) => { if (c) onPick(c); }} />
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Input
                autoFocus
                value={text}
                maxLength={MAX_TEXT}
                placeholder="e.g. a lakeside town for hiking"
                aria-label="Describe the place you would like"
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); suggest(); } }}
              />
            </div>
            <button
              type="button"
              onClick={suggest}
              disabled={!canSuggest}
              style={{ flex: 'none', background: GREEN, color: '#fff', border: 'none', borderRadius: 12, padding: '9px 14px', fontSize: 13.5, fontWeight: 700, cursor: canSuggest ? 'pointer' : 'default', opacity: canSuggest ? 1 : 0.45 }}
            >
              Suggest
            </button>
          </div>
          <div style={{ fontSize: 11.5, color: '#b3bbb6', marginTop: 5 }}>Jody also considers your route, dates and trip preferences.</div>

          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13, color: MUTED }}>
              <Dots /> Looking for places
            </div>
          )}

          {status === 'error' && !loading && (
            <div role="alert" style={{ marginTop: 10, fontSize: 13, color: '#cf5a3c' }}>{error}</div>
          )}

          {list && list.length === 0 && (
            <div style={{ marginTop: 10, fontSize: 13, color: MUTED }}>No good match near your route. Try describing it differently.</div>
          )}

          {list && list.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
              {list.map((s, i) => {
                const flag = getCountryFlag(s.country || undefined);
                const hint = travelHint(s.travel, target.action, replacingCity);
                // The card picks the place; the Google link under it is its own
                // control (a link can't sit inside the card's button).
                return (
                  <div
                    key={`${s.city}-${i}`}
                    className="spp-option"
                    onMouseEnter={() => preview(s)}
                    onMouseLeave={() => preview(null)}
                    onFocus={() => preview(s)}
                    onBlur={() => preview(null)}
                    style={{ background: '#fff', border: '1px solid rgba(20,40,30,0.10)', borderRadius: 12, overflow: 'hidden' }}
                  >
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => pick(s, i)}
                      aria-label={`Pick ${s.city}${s.country ? `, ${s.country}` : ''}`}
                      style={{ display: 'block', width: '100%', textAlign: 'left', font: 'inherit', background: 'transparent', border: 'none', padding: '10px 12px 4px', cursor: busy ? 'default' : 'pointer' }}
                    >
                      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: 700, fontSize: 14.5, color: INK }}>
                          {flag ? `${flag} ` : ''}{s.city}
                          {s.country && <span style={{ fontWeight: 500, color: MUTED }}>, {s.country}</span>}
                        </span>
                        {hint && <span style={{ fontSize: 12, fontWeight: 600, color: GREEN, whiteSpace: 'nowrap' }}>{hint}</span>}
                      </div>
                      {s.reason && <div style={{ fontSize: 12.5, color: MUTED, marginTop: 3, lineHeight: 1.35 }}>{s.reason}</div>}
                    </button>
                    <a
                      href={googleUrl(s)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => result && analytics.trackStopSuggestionLookedUp({ action: target.action, rank: i + 1, suggestion_count: result.suggestions.length, city: s.city, llm_request_id: result.requestId })}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, margin: '0 12px 9px', fontSize: 12, fontWeight: 600, color: MUTED, textDecoration: 'none' }}
                    >
                      Look it up on Google <ExternalIcon />
                    </a>
                  </div>
                );
              })}
              {result && result.forText === text.trim() && (
                <button type="button" onClick={suggest} disabled={!canSuggest} style={{ ...linkBtn, color: GREEN, alignSelf: 'flex-start' }}>Show other places</button>
              )}
            </div>
          )}
        </>
      )}

      <button type="button" onClick={onCancel} style={{ ...linkBtn, marginTop: 8, display: 'block' }}>Cancel</button>
      <style>{'.spp-option:has(button:hover:not(:disabled)){border-color:#157a55;background:#f7fbf9}.spp-option a:hover{color:#157a55;text-decoration:underline}'}</style>
    </div>
  );
};

export default StopPlacePicker;
